import { get } from 'lodash';
import React, { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { stripeErrorHandler } from 'shared-components/stripe/stripeErrorHandler';
import Contracts from '../../../api/api-contracts';
import Payments from '../../../api/api-payments';
import { Profile } from '../../../api/api-profile';
import { Payment } from '../../../components/src/elements-forms/payment-old';
import { paymentErrors } from '../app-bookings-current-details/data/payment-data';
import { PaymentErrorModal } from '../app-bookings-custom/modals/payment-error-modal';
import ProcessingPage from '../app-bookings-request/ProcessingPage';
import { checkCCStateInterval } from '../app-bookings-request/forms/bookingUtil';
import Button from '../elements-buttons/Button';
import SharedIntlProviderHOC from '../shared-components/hocs/sharedIntlProviderHOC';
import { BookingRequestSummary } from '../elements-summaries/booking-request-summary/BookingRequestSummary';

const UpdatePaymentMain = ({
  intl,
  normalizedContracts,
  paymentInfo,
  router,
  routeParams,
  accessToken,
  outstandingBalance,
  profile
}) => {
  const [newCard, setNewCard] = useState();
  const [stripe, setStripe] = useState();
  const [cardholdersName, setCardholdersName] = useState('');
  const [stripeElementComplete, setStripeElementComplete] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    (paymentInfo || [])[0]
  );
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    masterContract,
    spaceRentalContract,
    bookingObject,
    items: addOns
  } = normalizedContracts;
  const currency = spaceRentalContract?.meta?.listing?.prices[0]?.currency;

  const guestWillBeCharged =
    masterContract?.state === 'SETTLE_BALANCE_REQUIRED' &&
    outstandingBalance > 0;

  const addNewCard = (user) => {
    return new Promise((resolve, reject) => {
      stripe
        .createToken(newCard, { name: cardholdersName })
        .then((stripeTokenRes) => {
          if (stripeTokenRes.error) {
            setErrorModalMessage(stripeTokenRes.error.message);
            return;
          }

          const { token } = stripeTokenRes;
          const cardToken = token.id;
          const ssoid = user?.SSOid;

          // add card to Payments API.
          const locale = SharedIntlProviderHOC.getLocale();

          Payments.addCard(locale, ssoid, accessToken, cardToken, (res) => {
            if (res.error || 400 < res.statusCode) {
              const errorHead = res.error.errorCode
                ? intl.formatMessage(paymentErrors[res.error.errorCode])
                : res.error.message;

              const apiError =
                res.error.errorCode &&
                intl.formatMessage(paymentErrors[res.error.errorCode]);
              const stripeRawErr = get(res, 'error.raw.code', null);
              const stripeRawDeclineErr = get(
                res,
                'error.raw.decline_code',
                null
              );
              const stripeErrObj = stripeErrorHandler(
                stripeRawDeclineErr || stripeRawErr
              );
              const stripeRawMsg =
                stripeErrObj && intl.formatMessage({ ...stripeErrObj });
              const errorBody = apiError || stripeRawMsg || null;

              const errorMessage = {
                errorHead: errorHead,
                errorBody: errorBody
              };
              setIsLoading(false);
              setErrorModalMessage(errorMessage);
              reject(errorMessage);
            } else {
              setSelectedPaymentMethod(res);
              resolve(res);
            }
          });
        });
    });
  };

  const settleBalance = (payload) => {
    setPaymentProcessing(true);
    Contracts.createGuestSettleBalance(
      payload,
      masterContract?.control_id,
      accessToken,
      (res) => {
        if (res.error) {
          throw res.error;
        }

        checkCCStateInterval(masterContract?.control_id, accessToken, () => {
          router.push({
            pathname: `/inbox/${masterContract?.control_id}`
          });
        });
      }
    );
  };

  const savePaymentMethod = (payload) => {
    Payments.updatePaymentMethod(
      payload,
      masterContract?.control_id,
      accessToken,
      (res) => {
        if (res.error) {
          throw res.error;
        }
        router.push({
          pathname: `inbox/${routeParams?.bookingID}/payment-details`,
          state: {
            paymentUpdateSuccess: true
          }
        });
      }
    );
  };

  const paymentUpdateAnalytic = () => {
    analyticsIntegrations.trackEvent('payment method updated', {
      booking_id: routeParams?.bookingID,
      source: 'manage bookings',
      has_failed_payment: guestWillBeCharged
    });
  };

  const updatePayment = () => {
    setIsLoading(true);
    try {
      Profile.read('/v1/profiles/me', accessToken, async (user) => {
        if (!user || !accessToken) {
          location.hash = 'a=signup&source=msg';
          return;
        }

        let paymentMethod = selectedPaymentMethod;
        if (!paymentMethod) {
          paymentMethod = await addNewCard(user);
        }

        const payload = {
          guest_payment_card_id: paymentMethod.id,
          guest_payment_customer_id: paymentMethod.customer
        };

        if (guestWillBeCharged) {
          const settleBalancePayload = {
            ...payload,
            guest_sso_id: masterContract?.guest_sso_id,
            host_sso_id: masterContract?.host_sso_id,
            items: [
              {
                type: 'BOOKING',
                info: {
                  schedule: bookingObject?.schedule,
                  number_of_guests: bookingObject?.number_of_guests,
                  booking_id: masterContract?.control_id,
                  alcohol_consumed: bookingObject?.alcohol_consumed
                }
              },
              ...(addOns || []).map((addOn) => ({
                type: 'ITEM',
                info: {
                  item_id: addOn.item_id || addOn._id,
                  max_count: addOn.count,
                  ...(addOn.units && { count: addOn.units })
                }
              }))
            ]
          };
          settleBalance(settleBalancePayload);
        } else {
          savePaymentMethod(payload);
        }

        paymentUpdateAnalytic();
      });
    } catch (errorMsg) {
      setIsLoading(false);
      setErrorModalMessage(errorMsg);
    }
  };

  return paymentProcessing ? (
    <ProcessingPage />
  ) : (
    <>
      <div
        className='ps-container-lg-margin ps-container-fixed'
        style={{
          paddingTop: '64px'
        }}
      >
        <div className='ps-row'>
          <h2 className='h3 col-sm-12'>
            <FormattedMessage
              id='inbox.update-payment.update_payment_method'
              defaultMessage='Update payment method'
            />
          </h2>
          <p className='col-sm-12'>
            <FormattedMessage
              id='inbox.update_payment_main.edit_your_payment'
              defaultMessage='Edit your payment method for your scheduled payment of {total}.'
              values={{
                total: (
                  <FormattedNumber
                    value={outstandingBalance}
                    currency={currency}
                    style='currency'
                    minimumFractionDigits={outstandingBalance % 1 === 0 ? 0 : 2}
                    maximumFractionDigits={outstandingBalance % 1 === 0 ? 0 : 2}
                  />
                )
              }}
            />
          </p>
          <div className='margin-md-top margin-xl-bottom col-md-7 col-sm-12 left-column'>
            <h6 className='h6'>
              <FormattedMessage
                id='update_payment_main.pay_with'
                defaultMessage='Pay with'
              />
            </h6>
            <Payment
              bootstrapColClass='full-width'
              paymentInfo={paymentInfo}
              actionFn={(d) => {
                setCardholdersName(d.cardholders_name);
              }}
              handleListClickCb={(data, cb) => {
                setSelectedPaymentMethod(JSON.parse(data));
                cb && cb();
              }}
              returnStripeCardInstance={(card, stripe) => {
                setNewCard(card);
                setStripe(stripe);
              }}
              stripeElementChange={(event) => {
                setStripeElementComplete(event.complete);
              }}
              useBootstrapGrid={false}
            />
            <div className='flex space-between'>
              <Button
                disabled={isLoading}
                className='btn btn-default classic'
                onClick={() => {
                  router.push({
                    pathname: `/inbox/${routeParams?.bookingID}`
                  });
                }}
              >
                <FormattedMessage
                  id='inbox.update_payment_main.back'
                  defaultMessage='Back'
                />
              </Button>
              <Button
                category='primary'
                classNameString='btn btn-primary'
                isLoading={isLoading}
                disabled={
                  isLoading ||
                  (!selectedPaymentMethod &&
                    (!stripeElementComplete || !cardholdersName))
                }
                onClick={updatePayment}
              >
                {guestWillBeCharged ? (
                  <FormattedMessage
                    id='inbox.update_payment_main.confirm_and_pay'
                    defaultMessage='Confirm & pay'
                  />
                ) : (
                  <FormattedMessage
                    id='inbox.update_payment_main.save_payment_info'
                    defaultMessage='Save payment information'
                  />
                )}
              </Button>
            </div>
          </div>
          <div className='margin-md-top col-md-5 col-sm-12'>
            <BookingRequestSummary
              booking={normalizedContracts?.bookingObject}
              listing={normalizedContracts?.spaceRentalContract?.meta.listing}
              masterContract={normalizedContracts?.masterContract}
              addOnsContracts={normalizedContracts?.items}
              profile={profile}
              intl={intl}
            />
          </div>
        </div>
      </div>
      <PaymentErrorModal
        errorMsg={errorModalMessage}
        showModal={!!errorModalMessage}
        toggleModal={() => setErrorModalMessage('')}
      />
    </>
  );
};

export default UpdatePaymentMain;
